import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import api from '../services/api';
import { toast } from 'sonner';
import {
  Gavel, Play, Pause, Clock, XCircle, Send, Users, TrendingDown,
  Activity, MessageSquare, AlertTriangle, Bell, Shield, BarChart3,
  ArrowDown, Eye, Zap, Check, Trophy
} from 'lucide-react';

const POLL_MS = 3000;

export default function AuctionControlRoom({ eventRef }) {
  const [status, setStatus] = useState(null);
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [extendMins, setExtendMins] = useState(5);
  const [selectedItem, setSelectedItem] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const prevBidCount = useRef(0);
  const prevClosing = useRef('');

  // ── Poll ──
  const poll = useCallback(async () => {
    try {
      const [sRes, eRes] = await Promise.all([
        api.get(`/api/rfx/events/${eventRef}/live-status`),
        api.get(`/api/rfx/events/${eventRef}`),
      ]);
      const s = sRes.data;
      setStatus(s);
      setEvent(eRes.data);

      // Alerts
      const newAlerts = [];
      if (s.total_bids === prevBidCount.current && s.total_bids > 0 && s.remaining_seconds > 0) {
        newAlerts.push({ type: 'warning', text: 'No new bids recently', icon: AlertTriangle });
      }
      const inactive = (s.supplier_activity || []).filter(a => {
        if (!a.latest_bid) return true;
        return (Date.now() - new Date(a.latest_bid).getTime()) > 300000;
      });
      if (inactive.length > 0) {
        newAlerts.push({ type: 'warning', text: `${inactive.length} supplier(s) inactive (5+ min)`, icon: Users });
      }
      if (s.remaining_seconds > 0 && s.remaining_seconds < 60 && s.total_bids > prevBidCount.current) {
        newAlerts.push({ type: 'danger', text: 'Sniping detected — bid in final minute', icon: Zap });
      }
      setAlerts(newAlerts);

      // Extension detection
      if (s.closing_date && s.closing_date !== prevClosing.current && prevClosing.current) {
        toast.info(`Auction extended! New close: ${new Date(s.closing_date).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', timeZone: 'America/Los_Angeles' })}`);
      }
      prevClosing.current = s.closing_date;
      prevBidCount.current = s.total_bids;
    } catch {}
    setLoading(false);
  }, [eventRef]);

  useEffect(() => { poll(); const iv = setInterval(poll, POLL_MS); return () => clearInterval(iv); }, [poll]);

  // ── Countdown ──
  const [countdown, setCountdown] = useState({ h: 0, m: 0, s: 0, total: 0 });
  useEffect(() => {
    const tick = () => {
      if (!status?.closing_date) return;
      const diff = Math.max(0, (new Date(status.closing_date) - new Date()) / 1000);
      setCountdown({ h: Math.floor(diff / 3600), m: Math.floor((diff % 3600) / 60), s: Math.floor(diff % 60), total: diff });
    };
    tick(); const iv = setInterval(tick, 1000); return () => clearInterval(iv);
  }, [status?.closing_date]);

  // ── Actions ──
  const doAction = async (action, body = {}, confirm = true) => {
    if (confirm && !window.confirm(`${action} this auction?`)) return;
    try {
      const r = await api.post(`/api/rfx/events/${eventRef}/${action}`, body);
      toast.success(r.data.message || `${action} successful`);
      poll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleBroadcast = async () => {
    if (!message.trim()) return;
    setSending(true);
    try {
      const r = await api.post(`/api/rfx/events/${eventRef}/broadcast`, { message });
      toast.success(r.data.message);
      setMessage('');
      poll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    setSending(false);
  };

  if (loading) return <div className="flex items-center justify-center h-96"><div className="animate-spin rounded-full h-8 w-8 border-2 border-indigo-600 border-t-transparent" /></div>;
  if (!status) return <p className="text-center text-slate-400 py-12">Unable to load auction status.</p>;

  const isLive = status.status === 'OPEN';
  const isPaused = status.status === 'PAUSED';
  const isClosed = !isLive && !isPaused;
  const urgent = countdown.total < 300;
  const pad = (n) => String(n).padStart(2, '0');

  // Baseline & savings
  const baseline = event?.baseline_estimate || (event?.auction_items || []).reduce((s, i) => s + (i.estimated_price || 0) * (i.quantity || 1), 0);
  const currentBest = status.lowest_total || 0;
  const savings = baseline > 0 ? baseline - currentBest : 0;
  const savingsPct = baseline > 0 ? ((savings / baseline) * 100).toFixed(1) : 0;

  // Activity — time since last bid
  const timeSince = (iso) => {
    if (!iso) return 'Never';
    const sec = (Date.now() - new Date(iso).getTime()) / 1000;
    if (sec < 60) return `${Math.floor(sec)}s ago`;
    if (sec < 3600) return `${Math.floor(sec / 60)}m ago`;
    return `${Math.floor(sec / 3600)}h ago`;
  };

  return (
    <div className="flex flex-col gap-3" data-testid="auction-control-room-v2">
      {/* ═══════ HEADER — Command Bar ═══════ */}
      <div className={`rounded-2xl p-5 transition-colors ${isPaused ? 'bg-amber-900' : urgent ? 'bg-red-900' : 'bg-slate-900'} text-white`}>
        <div className="flex items-center justify-between">
          {/* Left: Event info */}
          <div className="flex items-center gap-3">
            <Gavel className="w-6 h-6" />
            <div>
              <h1 className="text-lg font-bold">{event?.title || eventRef}</h1>
              <span className="text-xs opacity-60">{eventRef} · {event?.category || ''}</span>
            </div>
          </div>

          {/* Center: Timer */}
          <div>
            <div className="flex items-center gap-2">
              <div className="text-center"><span className={`text-4xl font-mono font-black ${urgent ? 'text-red-300' : ''}`}>{pad(countdown.h)}</span><span className="text-[10px] block opacity-40">HRS</span></div>
              <span className="text-2xl font-mono opacity-30">:</span>
              <div className="text-center"><span className={`text-4xl font-mono font-black ${urgent ? 'text-red-300' : ''}`}>{pad(countdown.m)}</span><span className="text-[10px] block opacity-40">MIN</span></div>
              <span className="text-2xl font-mono opacity-30">:</span>
              <div className="text-center"><span className={`text-4xl font-mono font-black ${urgent ? 'animate-pulse text-red-300' : ''}`}>{pad(countdown.s)}</span><span className="text-[10px] block opacity-40">SEC</span></div>
            </div>
            <p className="text-[10px] text-center opacity-50 mt-0.5">
              {status?.closing_date && `Closes ${new Date(status.closing_date).toLocaleString('en-GB', { day:'2-digit', month:'short', hour:'2-digit', minute:'2-digit' })}`}
              {status?.timezone && ` (${status.timezone.replace(/_/g,' ')})`}
            </p>
          </div>

          {/* Right: Controls */}
          <div className="flex items-center gap-2">
            {/* Status badge */}
            {isLive && <span className="flex items-center gap-1.5 bg-emerald-500 px-3 py-1 rounded-full text-sm font-bold"><span className="relative flex h-2 w-2"><span className="animate-ping absolute h-full w-full rounded-full bg-white opacity-75"/><span className="relative rounded-full h-2 w-2 bg-white"/></span>LIVE</span>}
            {isPaused && <span className="bg-amber-500 px-3 py-1 rounded-full text-sm font-bold animate-pulse">PAUSED</span>}
            {isClosed && <span className="bg-slate-500 px-3 py-1 rounded-full text-sm font-bold">{status.status}</span>}

            {/* Action buttons */}
            {/* Action buttons — visible solid colors */}
            {isLive && <Button size="sm" className="bg-amber-500 hover:bg-amber-600 text-white font-semibold" onClick={() => doAction('pause')}><Pause className="w-3.5 h-3.5 mr-1" /> Pause</Button>}
            {isPaused && <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white font-semibold" onClick={() => doAction('resume')}><Play className="w-3.5 h-3.5 mr-1" /> Resume</Button>}
            <Button size="sm" className="bg-blue-500 hover:bg-blue-600 text-white font-semibold" onClick={() => doAction('extend', { minutes: extendMins })}>
              <Clock className="w-3.5 h-3.5 mr-1" /> Extend {extendMins} min
            </Button>
            <Button size="sm" className="bg-red-600 hover:bg-red-700 text-white font-semibold" onClick={() => doAction('close')}><XCircle className="w-3.5 h-3.5 mr-1" /> End Auction</Button>
          </div>
        </div>
      </div>

      {/* ═══════ Alerts Bar ═══════ */}
      {alerts.length > 0 && (
        <div className="flex gap-2">
          {alerts.map((a, i) => {
            const Icon = a.icon;
            return (
              <div key={i} className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium ${
                a.type === 'danger' ? 'bg-red-50 text-red-700 border border-red-200' : 'bg-amber-50 text-amber-700 border border-amber-200'}`}>
                <Icon className="w-3.5 h-3.5" />{a.text}
              </div>
            );
          })}
        </div>
      )}

      {/* ═══════ MAIN — Grid + Insights ═══════ */}
      <div className="flex gap-4 flex-1 min-h-0">
        {/* ── Left: Live Bid Grid ── */}
        <div className="flex-1 border rounded-2xl bg-white overflow-hidden flex flex-col">
          <div className="overflow-y-auto flex-1">
            <table className="w-full text-sm table-fixed">
              <thead className="sticky top-0 bg-slate-50 z-10">
                <tr className="border-b">
                  <th className="text-left px-3 py-2.5 font-semibold">Supplier</th>
                  <th className="text-right px-3 py-2.5 font-semibold w-32">Best Bid</th>
                  <th className="text-left px-3 py-2.5 font-semibold w-24">Position</th>
                  <th className="text-center px-2 py-2.5 font-semibold w-14">Bids</th>
                  <th className="text-left px-2 py-2.5 font-semibold w-20">Activity</th>
                  <th className="text-center px-2 py-2.5 font-semibold w-12">Trend</th>
                </tr>
              </thead>
              <tbody>
                {(status.supplier_activity || []).length === 0 && (
                  <tr><td colSpan={6} className="px-3 py-12 text-center text-slate-400">Waiting for bids...</td></tr>
                )}
                {(status.supplier_activity || []).sort((a, b) => a.best_total - b.best_total).map((sup, idx) => {
                  const isActive = sup.latest_bid && (Date.now() - new Date(sup.latest_bid).getTime()) < 120000;
                  const isSelected = selectedItem === sup.supplier_id;
                  return (
                    <tr key={sup.supplier_id} onClick={() => setSelectedItem(sup.supplier_id)}
                      className={`border-b cursor-pointer transition-colors ${
                        isSelected ? 'bg-indigo-50 border-l-4 border-l-indigo-500' :
                        isActive ? 'bg-emerald-50/50' : 'hover:bg-slate-50'}`}>
                      <td className="px-3 py-2.5">
                        <div className="flex items-center gap-1.5">
                          <div className={`w-2 h-2 rounded-full flex-shrink-0 ${isActive ? 'bg-emerald-500' : 'bg-slate-300'}`} />
                          <span className="font-medium truncate">{sup.supplier_name}</span>
                        </div>
                      </td>
                      <td className="px-3 py-2.5 text-right">
                        <span className="font-mono font-bold text-indigo-700">${sup.best_total?.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                      </td>
                      <td className="px-3 py-2.5">
                        {idx === 0 ? <Badge className="bg-emerald-100 text-emerald-700 text-[10px]"><Trophy className="w-3 h-3 mr-0.5 inline" />Lead</Badge>
                          : <span className="text-xs text-slate-500">#{idx + 1}</span>}
                      </td>
                      <td className="px-2 py-2.5 text-center font-bold text-xs">{sup.bid_count}</td>
                      <td className="px-2 py-2.5 text-xs text-slate-500 whitespace-nowrap">{timeSince(sup.latest_bid)}</td>
                      <td className="px-2 py-2.5 text-center">
                        {sup.bid_count > 1 ? <ArrowDown className="w-3.5 h-3.5 text-emerald-500 mx-auto" /> : <span className="text-slate-300">—</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {/* Grid footer */}
          <div className="bg-slate-50 border-t px-4 py-2 flex justify-between text-xs text-slate-500">
            <span>{status.active_suppliers} active supplier(s) · {status.total_bids} total bid(s)</span>
            <span>Lowest: <strong className="text-indigo-700">${status.lowest_total?.toLocaleString('en-US', { minimumFractionDigits: 2 }) || '—'}</strong></span>
          </div>
        </div>

        {/* ── Right: Insights Panel ── */}
        <div className="w-80 flex-shrink-0 flex flex-col gap-3">
          {/* Summary */}
          <div className="border rounded-2xl bg-white p-4">
            <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3">Event Summary</h3>
            <div className="grid grid-cols-2 gap-3">
              <div className="text-center"><p className="text-2xl font-bold text-slate-700">{status.active_suppliers}</p><p className="text-[10px] text-slate-400">Active Bidders</p></div>
              <div className="text-center"><p className="text-2xl font-bold text-slate-700">{status.total_bids}</p><p className="text-[10px] text-slate-400">Total Bids</p></div>
            </div>
          </div>

          {/* Savings Tracker */}
          {baseline > 0 && (
            <div className={`border rounded-2xl p-4 ${savings > 0 ? 'bg-emerald-50 border-emerald-200' : 'bg-white'}`}>
              <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3 flex items-center gap-1"><TrendingDown className="w-3.5 h-3.5" /> Savings Tracker</h3>
              <div className="space-y-2">
                <div className="flex justify-between text-sm"><span className="text-slate-500">Baseline</span><span className="font-mono">${baseline.toLocaleString()}</span></div>
                <div className="flex justify-between text-sm"><span className="text-slate-500">Current Best</span><span className="font-mono font-bold text-indigo-700">${currentBest.toLocaleString()}</span></div>
                <hr />
                <div className="flex justify-between text-sm font-bold">
                  <span className="text-emerald-700">Savings</span>
                  <span className="text-emerald-700">${savings.toLocaleString()} ({savingsPct}%)</span>
                </div>
                <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
                  <div className="h-full bg-emerald-500 rounded-full transition-colors" style={{ width: `${Math.min(parseFloat(savingsPct), 100)}%` }} />
                </div>
              </div>
            </div>
          )}

          {/* Activity Monitor */}
          <div className="border rounded-2xl bg-white p-4">
            <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2 flex items-center gap-1"><Activity className="w-3.5 h-3.5" /> Supplier Activity</h3>
            <div className="space-y-1.5 max-h-32 overflow-y-auto">
              {(status.supplier_activity || []).map((sup, i) => {
                const isActive = sup.latest_bid && (Date.now() - new Date(sup.latest_bid).getTime()) < 120000;
                return (
                  <div key={i} className="flex items-center justify-between text-xs">
                    <div className="flex items-center gap-1.5">
                      <div className={`w-1.5 h-1.5 rounded-full ${isActive ? 'bg-emerald-500' : 'bg-red-400'}`} />
                      <span className={isActive ? 'text-slate-700' : 'text-slate-400'}>{sup.supplier_name}</span>
                    </div>
                    <span className="text-slate-400">{timeSince(sup.latest_bid)}</span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Quick Actions */}
          <div className="border rounded-2xl bg-white p-4">
            <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Broadcast to Suppliers</h3>
            <p className="text-[10px] text-slate-400 mb-2">Send a message to all invited suppliers via email and their live auction screen.</p>
            <div className="space-y-2">
              <div className="flex gap-2">
                <Input value={message} onChange={e => setMessage(e.target.value)} placeholder="Type message to all suppliers..." className="flex-1 h-8 text-xs"
                  onKeyDown={e => e.key === 'Enter' && handleBroadcast()} />
                <Button size="sm" className="h-8 bg-indigo-600 hover:bg-indigo-700" onClick={handleBroadcast} disabled={sending || !message.trim()}>
                  <Send className="w-3 h-3" />
                </Button>
              </div>
            </div>
          </div>

          {/* Selected Supplier Detail */}
          {selectedItem && (() => {
            const sup = (status.supplier_activity || []).find(s => s.supplier_id === selectedItem);
            if (!sup) return null;
            return (
              <div className="border rounded-2xl bg-white p-4">
                <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Supplier Detail</h3>
                <p className="text-sm font-bold mb-1">{sup.supplier_name}</p>
                <div className="space-y-1 text-xs">
                  <div className="flex justify-between"><span className="text-slate-500">Best Total</span><span className="font-mono font-bold text-indigo-700">${sup.best_total?.toLocaleString()}</span></div>
                  <div className="flex justify-between"><span className="text-slate-500">Bids Placed</span><span className="font-bold">{sup.bid_count}</span></div>
                  <div className="flex justify-between"><span className="text-slate-500">Last Bid</span><span>{timeSince(sup.latest_bid)}</span></div>
                </div>
              </div>
            );
          })()}
        </div>
      </div>

      {/* ═══════ FOOTER — Activity Feed + Messages ═══════ */}
      <div className="flex gap-4">
        {/* Recent Activity */}
        <div className="flex-1 border rounded-2xl bg-white p-4 max-h-28 overflow-y-auto">
          <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2 flex items-center gap-1"><Activity className="w-3.5 h-3.5" /> Live Activity Feed</h3>
          {(status.recent_bids || []).length === 0 ? <p className="text-xs text-slate-400">Waiting for activity...</p> : (
            <div className="space-y-1">
              {(status.recent_bids || []).slice(0, 10).map((b, i) => (
                <div key={i} className="flex items-center gap-2 text-xs whitespace-nowrap">
                  <span className="text-slate-400 font-mono flex-shrink-0">{b.placed_at ? new Date(b.placed_at).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' }) : ''}</span>
                  <span className="font-medium truncate">{b.supplier_name || b.supplier_id}</span>
                  <span className="text-slate-500 flex-shrink-0">bid</span>
                  <span className="font-mono font-bold text-indigo-700 flex-shrink-0">${b.total_amount?.toLocaleString()}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Broadcast Messages Sent */}
        <div className="w-72 flex-shrink-0 border rounded-2xl bg-white p-4 max-h-28 overflow-y-auto">
          <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2 flex items-center gap-1"><MessageSquare className="w-3.5 h-3.5" /> Messages Sent</h3>
          {(status.broadcast_messages || []).length === 0 ? <p className="text-xs text-slate-400">No messages sent.</p> : (
            <div className="space-y-1">
              {(status.broadcast_messages || []).map((m, i) => (
                <div key={i} className="text-xs"><span className="text-slate-400">{m.sent_at ? new Date(m.sent_at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', timeZone: 'America/Los_Angeles' }) : ''}</span> — {m.message}</div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
